import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { ArrowLeft, Bell, ShoppingCart, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useAutoNarrate } from "../hooks/useAutoNarrate";

export default function BookDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const [book, setBook] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [buying, setBuying] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [owned, setOwned] = useState([]);
  const { register, Toggle: AutoplayToggle, AudioEl } = useAutoNarrate();

  const lang = i18n.language || "en";

  useEffect(() => { api.get(`/books/${id}?lang=${lang}`).then(({ data }) => setBook(data)); }, [id, lang]);
  useEffect(() => {
    api.get("/paypal/config").then(({ data }) => setCatalog(data?.catalog?.books || {})).catch(() => {});
    if (user) {
      api.get("/paypal/books/purchases").then(({ data }) => setOwned(data?.purchases || [])).catch(() => {});
    }
  }, [user]);

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/books/waitlist", { email, book_id: id });
      toast.success("You're on the list — we'll send a note when it ships.");
      setEmail("");
    } catch {
      toast.error("Could not join waitlist");
    } finally { setBusy(false); }
  };

  const buy = async (sku, label) => {
    if (!user) {
      toast.info("Sign in to complete your purchase");
      nav(`/signup?next=/books/${id}`);
      return;
    }
    setBuying(sku);
    try {
      const { data } = await api.post("/paypal/orders/create", { sku });
      if (!data?.approve_url) throw new Error("No PayPal URL");
      sessionStorage.setItem("dpa_pending_checkout", JSON.stringify({ sku, label, recurring: false, at: Date.now() }));
      window.location.assign(data.approve_url);
    } catch (e) {
      setBuying(null);
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  if (!book) return <div className="container-narrow py-20 text-center text-ink2">Loading…</div>;

  // SKUs applicable to this book: this book's own SKUs + the library bundle (visible on every book)
  const skus = Object.entries(catalog).filter(([, item]) => item.book_id === id || item.book_id === "*");
  const ownedFormats = new Set(owned.filter((o) => o.book_id === id || o.book_id === "*").map((o) => o.format));
  const available = book.status === "available";

  return (
    <div className="container-narrow py-12 md:py-16" data-testid="book-detail">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <Link to="/books" className="inline-flex items-center gap-1.5 text-ink2 hover:text-ink" data-testid="book-back">
          <ArrowLeft className="w-4 h-4" /> Back to bookshop
        </Link>
        <AutoplayToggle testid="book-autoplay-toggle" />
      </div>
      <AudioEl />

      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div className="card-book p-6">
          <div className="aspect-[3/4] rounded-xl overflow-hidden relative" style={{ background: book.color }}>
            <img src={book.cover} alt="" className="w-full h-full object-cover mix-blend-multiply opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent" />
            <div className="absolute bottom-5 left-5 right-5 text-cream">
              <div className="text-xs uppercase tracking-widest opacity-80 mb-1">{book.format}</div>
              <div className="font-heading text-2xl md:text-3xl leading-tight">{book.title}</div>
            </div>
          </div>
        </div>

        <div>
          <div className="label-eyebrow mb-3">{book.format}</div>
          <div ref={register(`book-${id}-title`, `${book.title}. ${book.tagline}`)}>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold leading-tight mb-3">{book.title}</h1>
            <p className="text-lg text-ink2 italic mb-6">{book.tagline}</p>
          </div>
          <p
            className="text-ink leading-relaxed mb-8"
            ref={register(`book-${id}-description`, book.description || "")}
            data-testid="book-description"
          >
            {book.description}
          </p>

          <div className="card-soft p-5 mb-6">
            <div className="label-eyebrow mb-3">A peek inside</div>
            <ul className="space-y-2 text-ink2">
              {book.preview?.map((p, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-marigold/15 text-marigold text-xs font-semibold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Buy buttons: per-book PayPal checkout */}
          {skus.length > 0 && (
            <div className="card-soft p-5 mb-4" data-testid="book-buy-panel">
              <div className="label-eyebrow mb-3 inline-flex items-center gap-2">
                <ShoppingCart className="w-3.5 h-3.5" />
                {available ? "Buy the book" : "Preorder now — ship on launch"}
              </div>
              <div className="space-y-2">
                {skus.map(([sku, item]) => {
                  const isOwned = ownedFormats.has(item.format);
                  return (
                    <div key={sku} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-paper border border-border" data-testid={`book-sku-row-${sku}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{item.name}</div>
                        <div className="text-xs text-ink2">{item.format} · ${item.price}</div>
                      </div>
                      {isOwned ? (
                        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-teal"><Check className="w-3.5 h-3.5" /> Owned</span>
                      ) : (
                        <button onClick={() => buy(sku, item.name)} disabled={buying === sku} className="btn-primary text-xs py-2" data-testid={`book-buy-${sku}`}>
                          {buying === sku ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting…</> : <>{available ? "Buy" : "Preorder"} · ${item.price}</>}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {!available && (
                <p className="text-[11px] text-ink2 mt-3 leading-relaxed">Preorders lock in launch pricing. You&apos;ll get an email the day your book ships, and your digital copy unlocks the moment we push it.</p>
              )}
            </div>
          )}

          {!available && (
            <form onSubmit={join} className="card-soft p-5 flex flex-col sm:flex-row gap-3" data-testid="book-waitlist-form">
              <div className="flex-1">
                <label className="text-sm font-medium block mb-1.5 inline-flex items-center gap-2"><Bell className="w-4 h-4" /> Notify me only (no charge)</label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="input-soft" data-testid="book-waitlist-email" />
              </div>
              <button className="btn-outline-ink self-end" disabled={busy} data-testid="book-waitlist-submit">
                {busy ? "..." : "Join waitlist"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
